/* eslint-disable react/forbid-prop-types */
import moment from 'moment';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useQuery } from 'react-query';
import { currencies, exchange } from '../../../../utilities/constants';
import { formatNumber, formatNumberWithComma } from '../../../../utilities/formatHelpers';
import ModalBodyLayout from '../../../shared/ModalBodyLayout';
import { YIELD_DEPOSIT_DETAILS } from '../../queries';

const ReceivedDetails = ({ details, rate }) => {
  const { t: translate } = useTranslation();

  const [duration, setDuration] = useState(0);

  const { data: contractDetails, refetch: refetchContractDetails } = useQuery(
    [YIELD_DEPOSIT_DETAILS, { contractId: details?.id }],
    {
      onSettled: (data, error) => {
        if (!error) {
          const activationDate = moment(data?.contractAttributes?.activatedAt);
          const expiryDate = moment(data?.product?.expirationDate);
          const days = expiryDate.diff(activationDate, 'days');

          setDuration(days);
        }
      },
      enabled: false,
    }
  );

  useEffect(() => {
    refetchContractDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details]);

  const renderTitle = () => (
    <div className="w-100 d-flex flex-row justify-content-between align-items-center">
      <p className="text-dark text-f5">{translate('yield_offer_details_modal_title')}</p>
      <div className="d-flex flex-row">
        <p className="text-f6 text-shadow me-2">{translate('oxygen_yield_current_BTC_label')}</p>
        <p className="text-f6 text-dark">{`${formatNumberWithComma(
          formatNumber(rate?.price, exchange.FIAT_VALUE)
        )} ${currencies.USDC.fullName}`}</p>
      </div>
    </div>
  );

  return (
    <ModalBodyLayout withBack={false} renderTitle={renderTitle} isScrollable>
      <div className="d-flex flex-row justify-content-between mt-4">
        <div className="d-flex flex-column">
          <p className="text-shadow text-f4 text-bold">
            {`${formatNumber(
              contractDetails?.contractAttributes?.annualizedYieldPct,
              exchange.FIAT_VALUE
            )}%`}
          </p>
          <p className="text-shadow text-f6">
            {translate('yield_offer_details_modal_annualized_label')}
          </p>
        </div>
        <div className="d-flex flex-column align-items-end">
          <p className="text-shadow text-f4 text-bold">
            {translate('yield_offer_details_modal_days_label').replace('__DAYS_NUMBER__', duration)}
          </p>
          <p className="text-shadow text-f6">
            {translate('yield_offer_details_modal_duration_label')}
          </p>
        </div>
      </div>
      <div className="d-flex flex-row justify-content-between mt-4">
        <div className="d-flex flex-column">
          <p className="text-shadow text-f5 text-bold">
            {contractDetails && contractDetails?.product?.linkedPrice
              ? `${formatNumberWithComma(
                  formatNumber(contractDetails?.product?.linkedPrice, exchange.FIAT_VALUE)
                )} ${currencies.USDC.fullName}`
              : `${formatNumberWithComma(formatNumber(0, exchange.FIAT_VALUE))} ${
                  currencies.USDC.fullName
                }`}
          </p>
          <p className="text-shadow text-f6">
            {translate('yield_offer_details_modal_linked_price_label')}
          </p>
        </div>
        <div className="d-flex flex-column align-items-end">
          <p className="text-shadow text-f5 text-bold">
            {details &&
              contractDetails?.product?.expirationDate &&
              moment(contractDetails?.product?.expirationDate, 'YYYYMMDDh:mm:ss').format(
                'DD/MM/YYYY'
              )}
          </p>
          <p className="text-shadow text-f6">
            {translate('yield_offer_details_modal_expiry_date_label')}
          </p>
        </div>
      </div>
      <div className="mt-5">
        <p className="text-shadow text-f6">
          {translate('yield_deposit_details_modal_amount_label')}
        </p>
        <p className="text-shadow text-f3 text-bold mt-2">
          {contractDetails && contractDetails?.contractAttributes?.amount
            ? `${formatNumberWithComma(
                formatNumber(contractDetails?.contractAttributes?.amount, exchange.FIAT_VALUE)
              )} ${currencies.USDC.fullName}`
            : `${formatNumberWithComma(formatNumber(0, exchange.FIAT_VALUE))} ${
                currencies.USDC.fullName
              }`}
        </p>
      </div>
      <div className="mt-4">
        <p className="text-shadow text-f6">{translate('you_received_label')}</p>
        <div className="d-flex flex-column bg-light rounded-extra p-3 mt-2">
          <div className="d-flex flex-row justify-content-between align-items-center">
            <div className="d-flex flex-row justify-content-start align-items-center">
              <div className="w-75 d-flex justify-content-center align-items-center">
                <img
                  src={`./assets/icons/${contractDetails?.product?.expirationCurrency}.svg`}
                  className="w-100"
                  alt={currencies.USDC.fullName}
                />
              </div>
              <p className="text-dark text-f5 text-bold text-nowrap ms-2">
                {contractDetails &&
                contractDetails?.contractAttributes[
                  `${contractDetails?.product?.expirationCurrency.toLowerCase()}PaidAmount`
                ]
                  ? `${formatNumberWithComma(
                      formatNumber(
                        contractDetails?.contractAttributes[
                          `${contractDetails?.product?.expirationCurrency.toLowerCase()}PaidAmount`
                        ],
                        exchange.CRYPTO_VALUE
                      )
                    )} ${contractDetails?.product?.expirationCurrency}`
                  : `${formatNumberWithComma(formatNumber(0, exchange.FIAT_VALUE))} ${
                      contractDetails?.product?.expirationCurrency
                    }`}
              </p>
            </div>
            <div className="bg-success-light rounded-1 px-2 py-1">
              <p className="text-success text-f6 text-uppercase">{translate('received_label')}</p>
            </div>
          </div>
          <div className="d-flex flex-row justify-content-between mt-4">
            <div className="d-flex flex-column justify-content-start">
              <p className="text-shadow text-f6">{translate('settlement_price_label')}</p>
              <p className="text-dark text-bold mt-1">
                {contractDetails && contractDetails?.contractAttributes?.btcBuyAt
                  ? `${formatNumberWithComma(
                      formatNumber(
                        contractDetails?.contractAttributes?.btcBuyAt,
                        exchange.FIAT_VALUE
                      )
                    )} ${currencies.USDC.fullName}`
                  : `${formatNumberWithComma(formatNumber(0, exchange.FIAT_VALUE))} ${
                      currencies.USDC.fullName
                    }`}
              </p>
            </div>
            <div className="d-flex flex-column justify-content-end align-items-end">
              <p className="text-shadow text-f6">{translate('payment_date_label')}</p>
              <p className="text-dar text-f5 bt-1">
                {contractDetails &&
                  contractDetails?.product?.expirationDate &&
                  moment(contractDetails?.product?.expirationDate, 'YYYYMMDDh:mm:ss').format(
                    'DD/MM/YYYY'
                  )}
              </p>
            </div>
          </div>
        </div>
      </div>
    </ModalBodyLayout>
  );
};

export default ReceivedDetails;

ReceivedDetails.propTypes = {
  details: PropTypes.object.isRequired,
  rate: PropTypes.object,
};

ReceivedDetails.defaultProps = {
  rate: {},
};
